<template>
  <div
    class="home-events"
    :class="{ scrolled: props.isScrolled }"
    data-t="home-events"
  >
    <div class="wrapper">
      <div class="title-wrapper">
        <h2 class="title">{{ t('sportHomePage.topEvents') }}</h2>
      </div>

      <div v-if="isTopEventsColumnReady" class="events" data-t="top-events">
        <EventOfTheDay v-if="eventOfTheDay" :event="eventOfTheDay" is-mobile />
        <EventItemCompact
          v-for="event in secondEvents"
          :key="event.id"
          :event="event"
          is-main-event
        />
        <EventItemCompact
          v-for="event in topEvents"
          :key="event.id"
          :event="event"
        />
        <StButton
          to="/mobile/sport/live"
          size="l"
          class="live-button"
          data-t="more-live-button"
        >
          {{ t('sportHomePage.liveNow') }}
          <StCounter :label="liveCounter" live bg-color="white" color="black" />
        </StButton>
      </div>
      <div v-else class="events-skeletons">
        <EventItemCompactSkeleton v-for="index in 10" :key="index" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  useEventOfTheDay,
  useTopEvents,
  useSecondEvents,
} from '../../composables'

const props = defineProps<{
  isScrolled: boolean
}>()

const { t } = useI18n()

const { eventOfTheDay } = useEventOfTheDay()
const { secondEvents } = useSecondEvents()
const { topEvents } = useTopEvents({
  duplicateEventIds: computed(() => {
    const ids: number[] = []
    if (eventOfTheDay.value) ids.push(eventOfTheDay.value.id)
    ids.push(...secondEvents.value.map((event) => event.id))
    return ids
  }),
})

const isTopEventsColumnReady = computed(
  () => secondEvents.value.length || topEvents.value.length,
)

const live = useLiveEvents()
const liveCounter = computed(() => live.value.length)
</script>

<style scoped>
.wrapper {
  position: relative;
  margin-top: var(--spacing-050);
  padding: 0 var(--spacing-200);
  border-radius: var(--border-radius-200) var(--border-radius-200) 0 0;

  &::before {
    content: '';

    position: absolute;
    z-index: -1;
    top: 0;
    right: 0;
    left: 0;

    display: block;

    height: 240px;

    background-color: var(--background-primary);
    border-radius: var(--border-radius-200) var(--border-radius-200) 0 0;

    transition: opacity 0.2s;

    mask-image: linear-gradient(180deg, #d9d9d9 0%, rgb(217 217 217 / 0%) 100%);
  }
}

.title-wrapper {
  position: sticky;
  z-index: 10;
  top: 111px;

  margin-right: calc(var(--spacing-200) * -1);
  margin-left: calc(var(--spacing-200) * -1);

  border-radius: var(--border-radius-200) var(--border-radius-200) 0 0;

  &::before {
    pointer-events: none;
    content: '';

    position: absolute;
    z-index: 1;
    right: 0;
    bottom: -32px;
    left: 0;

    height: 32px;

    opacity: 0;
    background: linear-gradient(
      var(--background-base) 0%,
      rgb(17 17 24 / 0%) 100%
    );

    transition: opacity 0.2s;
  }
}

.title {
  display: flex;
  align-items: center;

  margin: 0;
  padding: var(--spacing-150) var(--spacing-200);

  font: var(--mobile-title-2-semibold);
  color: var(--text-primary);

  border-bottom: var(--border-width-light, 0.5px) solid transparent;
}

.events,
.events-skeletons {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-075);
}

.live-button {
  margin: 0 auto;
  margin-top: var(--spacing-250);
}

.home-events {
  &.scrolled {
    .title-wrapper {
      background: var(--background-primary);

      &::before {
        opacity: 1;
      }
    }

    .title {
      border-bottom: var(--border-width-light, 0.5px) solid
        var(--border-primary, rgb(255 255 255 / 12%));
    }

    .wrapper {
      &::before {
        opacity: 0;
      }
    }
  }
}
</style>
